<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <base-alert-dialog
      :show="showRestoreSuccess"
      variant="success"
      title="Email delivery restored"
      message="You will receive notification emails again. Check your spam folder for the next one and mark it as not spam so future notifications reach your inbox."
      confirm-text="Got it"
      @confirm="showRestoreSuccess = false"
      @cancel="showRestoreSuccess = false"
    />

    <base-alert-dialog
      :show="alertDialog.show"
      :variant="alertDialog.variant"
      :title="alertDialog.title"
      :message="alertDialog.message"
      @confirm="onAlertConfirm"
      @cancel="alertDialog.show = false"
    />

    <div class="row">
      <div class="col">
        <card>
          <div class="row mb-3">
            <div class="col">
              <tab-rail
                :tabs="categories"
                :value="activeTabIndex"
                @input="switchTab"
              />
            </div>
          </div>

<!--          Profile settings-->
          <div v-if="activeTabIndex === 0">
            <div class="row">
              <div class="col-lg-3 text-center mb-4 pt-4">
                <image-upload
                  type="avatar"
                  :src="user.img"
                  select-text="Upload photo"
                  change-text="Change photo"
                  @change="handleAvatarChange"
                />
                <h4 class="text-white mt-3 mb-2">{{ user.first_name }} {{ user.last_name }}</h4>
                <div class="mt-3">
                  <label class="d-block mb-1">Role</label>
                  <badge>{{ user.role_name }}</badge>
                </div>
                <div class="mt-3">
                  <label class="d-block mb-1">Groups</label>
                  <badge v-for="(g, i) in visibleGroups" :key="'G' + i" class="mr-1 mb-1" type="primary">{{ g.name }}</badge>
                  <el-popover v-if="hiddenGroups.length > 0" trigger="hover" placement="bottom" width="220">
                    <div>
                      <badge v-for="(g, i) in hiddenGroups" :key="'H' + i" class="mr-1 mb-1" type="primary">{{ g.name }}</badge>
                    </div>
                    <badge slot="reference" type="default" class="mb-1">+{{ hiddenGroups.length }} more</badge>
                  </el-popover>
                </div>
                <div v-if="user.last_login" class="mt-3">
                  <label class="d-block mb-1">Last login</label>
                  <small class="text-white">{{ getDate(user.last_login) }}</small>
                </div>
              </div>
              <div class="col-lg-9">
                <collapsible-section title="Personal details" icon="tim-icons icon-single-02" :collapsible="false">
                  <div class="row">
                    <div class="col-md-6">
                      <base-input
                        type="text"
                        label="First name"
                        placeholder="First name"
                        v-model="user.first_name"
                      >
                      </base-input>
                    </div>
                    <div class="col-md-6">
                      <base-input
                        type="text"
                        label="Last name"
                        placeholder="Last name"
                        v-model="user.last_name"
                      >
                      </base-input>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6">
                      <base-input
                        type="text"
                        label="Nickname"
                        placeholder="Nickname"
                        v-model="user.nickname"
                      >
                      </base-input>
                    </div>
                    <div class="col-md-6">
                      <base-input label="Birthday">
                        <el-date-picker placeholder="Birthday"
                                        value-format="yyyy-MM-dd"
                                        v-model="user.birthday"
                                        :format="date_format_cal" :picker-options="{ firstDayOfWeek: first_day_of_week }"
                        ></el-date-picker>
                      </base-input>
                    </div>
                  </div>
                </collapsible-section>

                <collapsible-section title="Contact" icon="tim-icons icon-email-85" :collapsible="false">
                  <info-hint v-if="user.email_suppressed_at" variant="primary" panel icon="" class="mb-3 mt-0">
                    <div>Delivery to this address is blocked because your email provider reported a past message as spam. You will not receive booking notifications until it is restored.</div>
                    <base-button type="primary" size="sm" class="mt-2" :loading="restoringDelivery" @click="restoreEmailDelivery">Restore email delivery</base-button>
                  </info-hint>
                  <div class="row">
                    <div class="col-md-6">
                      <base-input
                        type="email"
                        label="Email address"
                        placeholder="Email"
                        :disabled="true"
                        v-model="user.email"
                      >
                      </base-input>
                    </div>
                    <div class="col-md-6">
                      <base-input
                        type="text"
                        label="Phone">
                        <phone-number-input ref="phoneInput"
                                            v-model="user.phone" @phone-update="handlePhoneUpdate"
                                            :default-country="countryCode || 'US'"
                        />
                      </base-input>
                    </div>
                  </div>
                </collapsible-section>

                <div class="d-flex justify-content-end">
                  <base-button v-on:click="updateProfile()" type="primary" :loading="loading">
                    Save
                  </base-button>
                </div>
              </div>
            </div>
          </div>

<!--          Availability-->
          <div class="row" v-if="activeTabIndex === 3">
            <div class="col">
              <fullCalendar :eventRender="eventRenderCallback" ref="calendar" :options="calendarOptions" />
            </div>
          </div>

          <div v-if="activeTabIndex === 1">
<!--            <div class="row">-->
<!--              <div class="col">-->
<!--                <label>Primary color</label>-->
<!--                <span-->
<!--                  v-for="item in sidebarColors"-->
<!--                >-->
<!--                  <button v-on:click="changeSidebarBackground(item)" :style="{'background-color': item.color}">color</button>-->
<!--                </span>-->

<!--              </div>-->
<!--            </div>-->
            <div class="row">
              <div class="col">
                <label>Site mode</label>
                <div class="switch-change-color mt-3">
                  <small class="label-switch text-white mr-2">LIGHT MODE</small>
                  <el-switch
                    v-model="user.site_mode"
                    :active-value="1"
                    :inactive-value="0"
                    active-color="#e14eca"
                    @change="toggleMode"
                  ></el-switch>
                  <small class="label-switch text-white label-right ml-2">DARK MODE</small>
                </div>
              </div>
            </div>
          </div>

          <div class="row"  v-if="activeTabIndex === 2">
            <div class="col-lg-4">
              <h4 slot="header" class="card-title">Change Password</h4>
              <base-alert v-if="error.length > 0" type="danger">
                <i class="fas fa-exclamation-triangle fa-2x"></i>
                {{error}}
              </base-alert>
              <base-alert v-if="message.length > 0" type="success">
                <i class="fas fa-exclamation-triangle fa-2x"></i>
                {{message}}
              </base-alert>
              <base-input
                type="password"
                label="Old Password"
                v-model="old_password"
              >
              </base-input>
              <base-input
                type="password"
                label="New Password"
                v-model="new_password"
              >
              </base-input>
              <base-button v-on:click="changePassword()" type="primary">
                Change Password
              </base-button>
            </div>
          </div>

          <div class="row mt-3" v-if="activeTabIndex === 1">
            <div class="col">
              <base-button v-on:click="updateProfile()" type="primary" :loading="loading">
                Save
              </base-button>
            </div>
          </div>
        </card>
      </div>

    </div>
  </div>
</template>

<script>
import { ImageUpload, BaseAlert } from 'src/components';
import { Loading, Switch } from 'element-ui';
import { API_LOCATION } from '../../api-config'
const axios = require('axios')
import { Auth } from 'aws-amplify'
import {formatGroupDate, formatGroupTime} from '@/plugins/dates'
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import Badge from "@/components/Badge";
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';
import TabRail from '@/components/TabRail.vue';
import CollapsibleSection from '@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection.vue';

export default {
  name: "MyProfile",
  components: {
    ImageUpload,
    Loading,
    BaseAlert,
    FullCalendar,
    Badge,
    PhoneNumberInput,
    TabRail,
    CollapsibleSection,
    [Switch.name]: Switch
  },
  computed: {
    visibleGroups() {
      return this.groups.slice(0, 4)
    },
    hiddenGroups() {
      return this.groups.slice(4)
    },
    categories() {
      return [
        { name: 'Profile settings', shortName: 'Profile' },
        { name: 'Site settings', shortName: 'Site' },
        { name: 'Change password', shortName: 'Password' },
      ];
    },
  },
  data() {
    return {
      countryCode: JSON.parse(localStorage.getItem('company')).country,
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week,
      sidebarColors: [
        { color: 'purple', active: false, value: 'primary' },
        { color: 'green', active: true, value: 'vue' },
        { color: 'blue', active: false, value: 'blue' },
        { color: 'limegreen', active: false, value: 'green' },
        { color: 'orange', active: false, value: 'orange' },
        { color: 'red', active: false, value: 'red' }
      ],
      activeTabIndex: 0,
      groups: [],
      user: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        birthday: '',
        phoneE164: ''
      },
      company: '',
      file: '',
      loading: false,
      restoringDelivery: false,
      showRestoreSuccess: false,
      alertDialog: {
        show: false,
        title: '',
        message: '',
        variant: 'success',
        redirectHome: false
      },
      old_password: '',
      new_password: '',
      error: '',
      message: '',
      date_format: '',
      date_format_cal: '',
      day_of_week: '',
      calendarOptions: {
        plugins: [
          // dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick,
        ],
        schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives',
        headerToolbar: {
          right: "prev,next,today",
        },
        height: 700,
        allDaySlot: false,
        scrollTime: '10:00:00',
        stickyHeaderDates: true,
        slotDuration: '00:15:00',
        slotLabelInterval: 15,
        slotLabelFormat: function (date) {
          let time_format = JSON.parse(localStorage.getItem('group')).time_format

          if(time_format === 'HH:mm') {
            let hour = date.date.hour
            let minute = '00'
            if (date.date.minute == 0){
              return hour + ':' + minute
            }
          } else {
            let hour = date.date.hour
            let dd = 'AM'
            if (date.date.minute == 0){
              if (parseInt(hour) >= 12) {
                hour = parseInt(date.date.hour) - 12;
                dd = 'PM'

              }
              if (hour == 0) {
                hour = 12;
              }
              return hour + ':00 ' + dd
            }
          }
        },
        editable: true,
        selectable: true,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventsSet: this.handleEvents,
      }
    }
  },
  methods: {
    handlePhoneUpdate(phoneData) {
      this.user.phoneE164 = phoneData.e164;
    },
    toggleMode(type) {
      let docClasses = document.body.classList;
      if (type) {
        docClasses.remove('white-content');
      } else {
        docClasses.add('white-content');
      }
    },
    handleEvents(events) {
      console.log(events)
    },
    handleEventClick(clickInfo) {
      console.log(clickInfo)
    },
    handleDateSelect(selectInfo) {
      console.log(selectInfo)
    },
    eventRenderCallback(event, element) {
      element.find('.fc-title').html(event.title + '<br>' + event.description);
    },
    switchTab(index){
      this.activeTabIndex = index
    },
    showAlert(title, message, variant, redirectHome = false){
      this.alertDialog = { show: true, title: title, message: message, variant: variant, redirectHome: redirectHome }
    },
    onAlertConfirm(){
      const redirect = this.alertDialog.redirectHome
      this.alertDialog.show = false
      if(redirect){
        this.$router.push('/')
      }
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async changePassword() {
      this.loading = true
      let user = await Auth.currentAuthenticatedUser()
      try {
        await Auth.changePassword(user, this.old_password, this.new_password)
        this.message = 'Password successfully changed!'
        this.error = ''
        this.loading = false
      } catch (err) {
        if(err.code === 'InvalidParameterException'){
          this.error = 'Password must be between at least 6 characters long.'
          this.message = ''
        } else if (err.code === 'NotAuthorizedException') {
          this.error = 'Old password is not correct.'
          this.message = ''
        } else if (err.code === 'LimitExceededException') {
          this.error = 'Too many requests. Please try again later.'
          this.message = ''
        }
        this.loading = false
      }
    },
    getDate(d){
      if(d == null || d == undefined){
        return
      }
      return formatGroupDate(d) + ', ' + formatGroupTime(d)
    },
    async getProfile(){
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'users/me'

      axios.get(url, config)
        .then(response => {
          this.user = response.data.profile
          this.company = response.data.company
          this.groups = response.data.groups
          this.date_format = JSON.parse(localStorage.getItem('group')).date_format
          this.date_format_cal = JSON.parse(localStorage.getItem('group')).date_format
          this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
          this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
          if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
            this.day_of_week = 'ddd, '
          }
          localStorage.setItem('companyId', response.data.companyId)
          localStorage.setItem('profile', JSON.stringify(this.user))
          localStorage.setItem('company', JSON.stringify(response.data.company))
          localStorage.setItem('permissions', JSON.stringify(response.data.permissions))
          localStorage.setItem('permissionLevels', JSON.stringify(response.data.permissionLevels || {}))
          localStorage.setItem('mode', this.user.site_mode)
          this.loading = false

        })
    },
    async restoreEmailDelivery() {
      this.restoringDelivery = true
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = API_LOCATION + 'users/me/restore-email-delivery'
        await axios.post(url, {}, config)
        this.showRestoreSuccess = true
        await this.getProfile()
      } catch (err) {
        let message = 'Could not restore email delivery, try again later'
        const raw = err.response?.data
        let parsed = raw
        if (typeof raw === 'string') {
          try { parsed = JSON.parse(raw) } catch (e) { parsed = { error: raw } }
        }
        if (parsed?.error) message = parsed.error
        this.$notify({ type: 'danger', message: message, icon: 'tim-icons icon-alert-circle-exc' })
      } finally {
        this.restoringDelivery = false
      }
    },
    async updateProfile() {
      this.loading = true
      await this.saveFile()
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'users/me'
      let data = this.user
      data.phone  = this.user.phoneE164 || this.user.phone
      axios.post(url, data, config)
        .then(response => {
          this.showAlert('Profile updated', 'Your profile has been saved.', 'success')
          this.getProfile()

          // axios.get(url, config)
          //   .then(response => {
          //     let user = response.data.profile
          //     localStorage.setItem('companyId', response.data.companyId)
          //     localStorage.setItem('profile', JSON.stringify(user))
          //     localStorage.setItem('company', JSON.stringify(response.data.company))
          //     localStorage.setItem('permissions', JSON.stringify(response.data.permissions))
          //
          //     this.getProfile()
          //     this.loading = false
          //   })
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('No access', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', true)
          } else {
            this.showAlert('Something went wrong', 'Your profile could not be saved. Try again later.', 'danger')
          }
        })
    },
    handleAvatarChange(file) {
      this.file = file || ''
    },
    async saveFile() {
      if(this.file != ''){
        this.uploadUrl = await this.getSignedURL(this.file)
        let uploaded = await this.uploadFile(this.file)
        //Get the image URL
        let imgUrl = this.uploadUrl.split("?")
        this.user.img = imgUrl[0]
      }
    },
    uploadFile (file) {
      let url = this.uploadUrl
      let config = { headers: {
          'Content-Type': file.type
        }}

      let payload = file
      return new Promise ( function(resolve) {
        axios.put(url, payload, config)
          .then((res) => {
            resolve(res)
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    async getSignedURL (file) {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'upload'

      let payload = {
        filePath: file.name,
        contentType: file.type
      }

      return new Promise ( function(resolve) {
        axios.post(url, payload, config)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
  },
  mounted(){
    this.getProfile()
    if (this.$route.query.tab !== undefined) {
      this.switchTab(Number(this.$route.query.tab));
    }
  },
  watch: {
    '$route.query.tab'(newTab) {
      if (newTab !== undefined) {
        this.switchTab(Number(newTab));
      }
    }
  }

}
</script>

<style >

</style>
